import {
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isNotFoundError,
  isSuccess,
} from '@/context/services/type';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import type { InvoiceReportsSearchRefetch } from '@/features/invoice_reports/types/search';
import { useTranslation } from '@/i18n';
import { isCommonError, isObjectRecord } from '@/libs/typeguard/isError';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import type {
  CommonError,
  OfficeInvoiceReportSettingsImportInvoiceReportSampleCsvPaths,
} from 'ap-openapi';
import { useImportInvoiceReports } from 'ap-openapi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type {
  DetailDrawerContentDetailProps,
  PaymentRequestImportForm,
  PaymentRequestUploadFile,
  UsePaymentRequestImportDrawerReturn,
} from '../types';

function isFileEmpty(file: { size?: number } | undefined): boolean {
  if (!file) return true;
  return file.size === undefined || file.size === 0;
}

function extractApiErrorMsg(data: unknown, fallback: string): string {
  const errorData: CommonError | Record<string, unknown> = isObjectRecord(data)
    ? data
    : {};
  return isCommonError(errorData) && Array.isArray(errorData.messages)
    ? errorData.messages.join('') || fallback
    : fallback;
}

/**
 * Encapsulates form state, validation, and handlers for PaymentRequestImportDrawer.
 * Same pattern as ImportTransactionDetailOptionDrawer: validate on change, set file.errorMessage
 * for upload UI, defer setValue via queueMicrotask to avoid updating form state in the same tick.
 *
 * Import flow - success: close drawer, refetch, toast, page 1; failure: keep drawer, toast.
 */
export function usePaymentRequestImportDrawer(
  open: boolean,
  onClose: () => void,
  onImportSuccess: () => void,
  canAgentEntry = false,
  importInvoiceReportSampleCsvPaths?: OfficeInvoiceReportSettingsImportInvoiceReportSampleCsvPaths,
  refetch?: InvoiceReportsSearchRefetch
): UsePaymentRequestImportDrawerReturn {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const [errorMsg, setErrorMsg] = useState('');
  const [isFileValid, setIsFileValid] = useState(false);

  const { mutateAsync: importInvoiceReports, isPending: isImporting } =
    useImportInvoiceReports();

  const methods = useForm<PaymentRequestImportForm>({
    defaultValues: {
      file: [],
      ...(canAgentEntry && { target_office_member_id: undefined }),
    },
  });
  const { setValue, reset, control, getValues } = methods;

  const resetFormState = useCallback(() => {
    reset(
      canAgentEntry
        ? { file: [], target_office_member_id: undefined }
        : { file: [] }
    );
    setErrorMsg('');
    setIsFileValid(false);
  }, [reset, canAgentEntry]);

  // Reset file when drawer closes (success, cancel, or X)
  useEffect(() => {
    if (!open) {
      resetFormState();
    }
  }, [open, resetFormState]);

  const handleClose = useCallback(() => {
    resetFormState();
    onClose();
  }, [resetFormState, onClose]);

  const onChange = useCallback(
    (file: PaymentRequestUploadFile | PaymentRequestUploadFile[]) => {
      const firstFile = Array.isArray(file) ? file[0] : file;
      if (!firstFile) return;
      const empty = isFileEmpty(firstFile);
      firstFile.errorMessage = empty
        ? t('invoice_reports.list_import_drawer.import_empty_error')
        : '';
      setIsFileValid(!empty);
      queueMicrotask(() => setValue('file', [firstFile]));
    },
    [t, setValue]
  );

  const uploader = useMemo(
    () => ({
      append: false,
      description: t('invoice_reports.list_import_drawer.file_format'),
      accept: '.csv',
      name: 'file' as const,
      control,
      onChange,
    }),
    [t, control, onChange]
  );

  const file = useWatch({ control, name: 'file', defaultValue: [] });
  const hasFile = Array.isArray(file) && file.length > 0;

  const targetOfficeMemberId = useWatch({
    control,
    name: 'target_office_member_id',
    defaultValue: undefined,
  });
  const hasValidApplicant =
    !canAgentEntry ||
    (typeof targetOfficeMemberId === 'string' &&
      targetOfficeMemberId.length > 0);

  const handleImport = useCallback(async () => {
    setErrorMsg('');
    const files = getValues('file');
    const firstFile = Array.isArray(files) ? files[0] : undefined;
    // Support Ant Design Upload format (originFileObj) and raw File/Blob
    const blob =
      firstFile?.originFileObj ??
      (firstFile instanceof File || firstFile instanceof Blob
        ? firstFile
        : undefined);
    if (!blob) {
      setErrorMsg(t('invoice_reports.list_import_drawer.import_empty_error'));
      notify.error({
        title: t('invoice_reports.list_import_drawer.import_empty_error'),
        duration: 3,
      });
      return;
    }

    try {
      const response = await importInvoiceReports({
        data: {
          file: blob,
          ...(canAgentEntry && {
            target_office_member_id:
              getValues('target_office_member_id') ?? null,
          }),
        },
      });
      // Custom client resolves with response for 4xx (does not throw)
      const status = response?.status;

      if (isSuccess(status)) {
        onImportSuccess();
        notify.success({
          title: t('invoice_reports.list_import_drawer.import_success_toast'),
          duration: 3,
        });
        return;
      }

      const fallbackMsg = t(
        'invoice_reports.list_import_drawer.import_error_common_toast'
      );

      if (isAuthError(status) || isForbiddenError(status)) {
        const apiMsg = extractApiErrorMsg(response?.data, fallbackMsg);
        await handleCheckPermission({
          fnCheckPermission: refetch,
          onCheckSuccess: () => {
            notify.error({ title: toHtmlNode(apiMsg), duration: 5 });
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
        return;
      }

      if (canAgentEntry && isNotFoundError(status)) {
        notify.error({
          title: t(
            'invoice_reports.proxy_applicant.import_no_permission_error'
          ),
          duration: 3,
        });
        return;
      }

      const errorMsg = isInvalidError(status)
        ? extractApiErrorMsg(response?.data, fallbackMsg)
        : fallbackMsg;

      notify.error({ title: toHtmlNode(errorMsg), duration: 5 });
    } catch {
      // 5xx, network errors - Keep drawer open
      notify.error({
        title: t(
          'invoice_reports.list_import_drawer.import_error_common_toast'
        ),
        duration: 5,
      });
    }
  }, [
    canAgentEntry,
    getValues,
    handleCheckPermission,
    importInvoiceReports,
    notify,
    onImportSuccess,
    refetch,
    t,
  ]);

  // Sample CSV: URLs from API settings (payee + instant_payee, utf_8 + shift_jis) used as normal <a href> in Detail.
  const detailDrawerProps: DetailDrawerContentDetailProps = useMemo(
    () => ({
      importInvoiceReportSampleCsvPaths,
    }),
    [importInvoiceReportSampleCsvPaths]
  );

  return {
    methods,
    handleClose,
    detailDrawerProps,
    hasValidApplicant,
    uploader,
    hasFile,
    isFileValid,
    handleImport,
    errorMsg,
    setErrorMsg,
    isImporting,
  };
}
